import { Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { suppressTabbingToNextCell, TemplateRendererComponent } from "@coso/grid";
import { LoadingService } from "@coso/loading";
import { DialogComponent, DialogType, IS_SAAS } from "@coso/utility";
import { ColDef, GridOptions, GridApi } from "ag-grid-community";
import { PageChangedEvent } from "ngx-bootstrap/pagination";
import { combineLatest } from "rxjs";
import { filter, take, tap } from "rxjs/operators";
import { IConfigurationDto, ServiceStatus } from "../../../models/auto-generated";
import { AConfigurationsFacade } from "../../../services/a-configurations.facade";
import { AUserPermissionsService } from "../../../services/a-user-permissions.service";
import { ConfigurationService } from "../../../services/configuration.service";
import { QuickActionsService } from "../../../services/quick-actions.service";
import { ReplicationService } from "../../../services/replication.service";
import { handleError } from "../../../utils/handle-error";
import { isRunning } from "../../../utils/is-running";
import { ListConfigurationsActionsCellRendererComponent } from "./list-configurations-actions-cell-renderer/list-configurations-actions-cell-renderer.component";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";

@Component({
  standalone:false,
  templateUrl: "./list-configurations.component.html",
  styleUrls: ["./list-configurations.component.scss"]
})
export class ListConfigurationsComponent implements OnInit, OnDestroy {
  @ViewChild("configurationId", { static: true }) configurationId: TemplateRef<any>;
  @ViewChild("queueStatus", { static: true }) queueStatus: TemplateRef<any>;
  @ViewChild("status", { static: true }) status: TemplateRef<any>;
  @ViewChild("deleteConfirmation", { static: true }) deleteConfirmation: TemplateRef<any>;

  successMessage: string;
  errorMessage: string;
  isReadOnly: boolean;
  isRunning = isRunning;
  Status = ServiceStatus;

  columnDefs: ColDef[];
  gridOptions: GridOptions;
  private gridApi: GridApi;
  pageResult$ = this.configurationsFacade.result$;
  itemsPerPage = 10;
  currentPage = 1;
  quickFilterText: string;
  recordsTotal: number;  
  selectedConfiguration: IConfigurationDto;

  constructor(
    @Inject(IS_SAAS) public isSaaS: boolean,
    public userPermissionsService: AUserPermissionsService,
    public loadingService: LoadingService,
    private quickActionsService: QuickActionsService,
    private modalService: NgbModal,
    private configurationService: ConfigurationService,
    private configurationsFacade: AConfigurationsFacade,
    private replicationService: ReplicationService
  ) {}

  ngOnInit(): void {
    this.successMessage = history.state ? history.state.message : undefined;
    this.userPermissionsService.hasLimitedAccess$.subscribe((hasLimitedAccess) => (this.isReadOnly = hasLimitedAccess));
    this.gridOptions = {
      context: { parentComponent: this },
      rowHeight: 40,
      suppressRowClickSelection: true,
      defaultColDef: { minWidth: 100, sortable: false, resizable: true, suppressKeyboardEvent: suppressTabbingToNextCell },
      getRowId: (params) => params.data.id,
      onGridReady: (event) => {
        this.gridApi = event.api;
        this.userPermissionsService.isCustomerAgent$.subscribe((isCustomerAgent) => {
          event.api.setColumnsVisible(["tenantId"], isCustomerAgent);
          event.api.sizeColumnsToFit();
        });
      }
    };
    this.columnDefs = [
      {
        headerName: "Id",
        valueGetter: (params) => ({ id: params.data.id, legacyId: params.data.legacyId }),
        cellRenderer: TemplateRendererComponent,
        cellRendererParams: { ngTemplate: this.configurationId }
      },
      { headerName: "Tenant Id", field: "tenantId" },
      { headerName: "Name", field: "name" },
      {
        headerName: "Status",
        valueGetter: (params) => ({ status: params.data.status, hasIssues: params.data.hasIssues }),
        cellRenderer: TemplateRendererComponent,
        cellRendererParams: { ngTemplate: this.status }
      },
      {
        headerName: "Process Queues",
        valueGetter: (params) => ({ status: params.data.status, queues: params.data.queues }),
        minWidth: 200,
        cellRenderer: TemplateRendererComponent,
        cellRendererParams: { ngTemplate: this.queueStatus }
      },
      {
        headerName: "Actions",
        valueGetter: (params) => ({ status: params.data.status, hasIssues: params.data.hasIssues, groupStorage: params.data.groupStorage }),
        minWidth: 340,
        maxWidth: 340,
        resizable: false,
        cellRenderer: ListConfigurationsActionsCellRendererComponent
      }
    ];
    this.loadConfigurations();
  }

  private loadConfigurations() {
    this.configurationsFacade.load(1, this.itemsPerPage, this.quickFilterText);
    this.loadingService.start(`Loading configurations`, "loadConfigurations");
    combineLatest([
      this.configurationsFacade.result$.pipe(
        filter((i) => !!i),
        take(1),
        tap((i) => (this.recordsTotal = i.totalNumberOfItems)),
        tap(() => this.loadingService.stop("loadConfigurations"))
      ),
      this.configurationsFacade.error$.pipe(
        filter((i) => !!i),
        take(1),
        tap((errorMessage) => {
          this.errorMessage = errorMessage;
          this.loadingService.stop("loadConfigurations");
        })
      )
    ]).subscribe();
  }

  async ngOnDestroy() {
    await this.configurationsFacade.dispose();
  }

  onPaginationChanged(event: PageChangedEvent): void {
    this.itemsPerPage = event.itemsPerPage;
    this.currentPage = event.page;
    this.configurationsFacade.load(event.page, event.itemsPerPage, this.quickFilterText);
  }

  onQuickFilterChanged() {
    this.currentPage = 1;
    this.configurationsFacade.load(this.currentPage, this.itemsPerPage, this.quickFilterText);
  }

  onQuickFilterClear() {
    this.currentPage = 1;
    this.quickFilterText = undefined;
    this.configurationsFacade.load(this.currentPage, this.itemsPerPage, this.quickFilterText);
  }

  startReplication(dto: IConfigurationDto) {
    this.errorMessage = this.successMessage = undefined;
    this.loadingService.start(`Starting replication process`, "startReplication");
    this.replicationService.start(dto.id).subscribe(
      () => {
        this.successMessage = `Successfully scheduled configuration '${dto.name}' for starting replication. This action may take a while.`;
        // Only reload content for SaaS environment (no signalR support)
        if (this.isSaaS) {
          this.configurationsFacade.load(this.currentPage, this.itemsPerPage, this.quickFilterText);
        }
        this.loadingService.stop("startReplication");
      },
      (error) => {
        this.redrawRow(dto.id);
        this.errorMessage = `Failed to start replication due to: ${handleError(error)}`;
        this.loadingService.stop("startReplication");
      }
    );
  }

  stopReplication(dto: IConfigurationDto) {
    this.errorMessage = this.successMessage = undefined;
    const modalRef = this.modalService.open(DialogComponent, {
      keyboard: false,
      ariaLabelledBy: "modalTitle"
    });

    modalRef.dismissed.subscribe(() => this.redrawRow(dto.id));

    modalRef.componentInstance.options = {
      titleText: "Stop replication process",
      type: DialogType.Warning,
      contentText: "Are you sure you want to stop the replication process?",
      rightButtons: [
        {
          label: "No",
          type: "button",
          class: "btn btn-dark mr-2",
          onClick: () => {
            modalRef.dismiss();
            this.redrawRow(dto.id);
          }
        },
        {
          label: "Yes",
          type: "button",
          class: "btn btn-primary",
          onClick: () => {
            this.loadingService.start(`Stopping replication process`, "stopReplication");
            this.replicationService.stop(dto.id).subscribe(
              () => {
                this.successMessage = `Successfully scheduled configuration '${dto.name}' for stopping replication. This action may take a while.`;
                // Only reload content for SaaS environment (no signalR support)
                if (this.isSaaS) {
                  this.configurationsFacade.load(this.currentPage, this.itemsPerPage, this.quickFilterText);
                }
                this.loadingService.stop("stopReplication");
                modalRef.dismiss();
              },
              (error) => {
                this.redrawRow(dto.id);
                this.errorMessage = `Failed to start replication due to: ${handleError(error)}`;
                this.loadingService.stop("stopReplication");
              }
            );
          }
        }
      ],
      onClose: () => {
        modalRef.dismiss();
        this.redrawRow(dto.id);
      }
    };
  }

  confirmDeletion(dto: IConfigurationDto): void {
    this.errorMessage = this.successMessage = undefined;
    this.selectedConfiguration = dto;

    const modalRef = this.modalService.open(DialogComponent, {
      keyboard: false,
      ariaLabelledBy: "modalTitle"            
    });
    modalRef.componentInstance.options = {
      titleText: "Delete configuration",
      type: DialogType.Warning,                  
      contentTemplateRef: this.deleteConfirmation,      
      rightButtons: [
        { label: "No", type: "button", class: "btn btn-dark mr-2", onClick: () => modalRef.dismiss() },
        {
          label: dto.sscEnabled ? "Yes, delete anyway" : "Yes",
          type: "button",
          class: "btn btn-primary",
          onClick: () => {
            this.loadingService.start(`Deleting configuration`, "deleteInstance");
            this.configurationService.delete(dto.id).subscribe(
              () => {
                this.successMessage = `Configuration '${dto.name}' has been deleted successfully.`;
                this.currentPage = 1;
                this.configurationsFacade.load(1, this.itemsPerPage, this.quickFilterText);
                this.loadingService.stop("deleteInstance");
                modalRef.dismiss();
              },
              (error) => {
                this.errorMessage = `Failed to delete configuration '${dto.name}' due to: ${handleError(error)}`;
                this.loadingService.stop("deleteInstance");
                modalRef.dismiss();
              }
            );
          }
        }
      ],
      onClose: () => modalRef.dismiss()
    };
  }

  forceFullIteration(dto: IConfigurationDto): void {
    this.errorMessage = this.successMessage = undefined;
    const modalRef = this.modalService.open(DialogComponent, {
      keyboard: false,
      ariaLabelledBy: "modalTitle"
    });
    modalRef.componentInstance.options = {
      titleText: "Force full iteration",
      type: DialogType.Warning,
      contentText: "Are you sure you want to reload the whole security model and check all SharePoint document locations? This action cannot be reversed and may take a while.",
      rightButtons: [
        { label: "No", type: "button", class: "btn btn-dark mr-2", onClick: (modalRef) => modalRef.dismiss() },
        {
          label: "Yes",
          type: "button",
          class: "btn btn-primary",
          onClick: () => {
            this.loadingService.start("Forcing full iteration");
            this.quickActionsService.forceFullIteration(dto.id).subscribe(
              () => {
                this.successMessage = `Full iteration has been forced successfully for configuration '${dto.name}'.`;
                this.loadingService.stop();
                modalRef.dismiss();
              },
              (error) => {
                this.errorMessage = `Failed to force full iteration for configuration '${dto.name} due to: ${handleError(error)}`;
                this.loadingService.stop();
                modalRef.dismiss();
              }
            );
          }
        }
      ],
      onClose: () => modalRef.dismiss()
    };
  }

  clearSpPermissionsCache(dto: IConfigurationDto): void {
    this.errorMessage = this.successMessage = undefined;
    const modalRef = this.modalService.open(DialogComponent, {
      keyboard: false,
      ariaLabelledBy: "modalTitle"
    });
    modalRef.componentInstance.options = {
      titleText: "Clear SharePoint Permission Cache",
      type: DialogType.Warning,
      contentText: "Are you sure you want to force overwriting permissions even when there is no change from the Dynamics 365 side? This action cannot be reversed and may take a while.",
      rightButtons: [
        { label: "No", type: "button", class: "btn btn-dark mr-2", onClick: () => modalRef.dismiss() },
        {
          label: "Yes",
          type: "button",
          class: "btn btn-primary",
          onClick: () => {
            this.loadingService.start("Clearing SharePoint Permissions Cache");
            this.quickActionsService.clearSpPermissionsCache(dto.id).subscribe(
              () => {
                this.successMessage = `SharePoint permissions cache has been cleared successfully for configuration '${dto.name}'.`;
                this.loadingService.stop();
                modalRef.dismiss();
              },
              (error) => {
                this.errorMessage = `Failed to clear SharePoint permissions cache for configuration '${dto.name}' due to: ${handleError(error)}`;
                this.loadingService.stop();
                modalRef.dismiss();
              }
            );
          }
        }
      ],
      onClose: () => modalRef.dismiss()
    };
  }

  clearSpGroupCache(dto: IConfigurationDto): void {
    this.errorMessage = this.successMessage = undefined;
    const modalRef = this.modalService.open(DialogComponent, {
      keyboard: false,
      ariaLabelledBy: "modalTitle"
    });
    modalRef.componentInstance.options = {
      titleText: "Clearing SharePoint Group Cache",
      type: DialogType.Warning,
      contentText: "Are you sure you want to remove all cached groups and group members from your local database? This action cannot be reversed.",
      rightButtons: [
        { label: "No", type: "button", class: "btn btn-dark mr-2", onClick: () => modalRef.dismiss() },
        {
          label: "Yes",
          type: "button",
          class: "btn btn-primary",
          onClick: () => {
            this.loadingService.start("Clearing SharePoint Group Cache");
            this.quickActionsService.clearSpGroupCache(dto.id).subscribe(
              () => {
                this.successMessage = `SharePoint group cache has been cleared successfully for configuration '${dto.name}'.`;
                this.loadingService.stop();
                modalRef.dismiss();
              },
              (error) => {
                this.errorMessage = `Failed to clear SharePoint group cache for configuration '${dto.name}' due to: ${handleError(error)}`;
                this.loadingService.stop();
                modalRef.dismiss();
              }
            );
          }
        }
      ],
      onClose: () => modalRef.dismiss()
    };
  }

  clearSdkMessageProcessingSteps(dto: IConfigurationDto): void {
    this.errorMessage = this.successMessage = undefined;
    const modalRef = this.modalService.open(DialogComponent, {
      keyboard: false,
      ariaLabelledBy: "modalTitle"
    });
    modalRef.componentInstance.options = {
      titleText: "Clear Sdk Message Processing Steps",
      type: DialogType.Warning,
      contentText:
        "Are you sure you want to unregister Sdk message processing steps so that you are able to delete the solution from Dynamics 365? This action cannot be reversed and may take a while.",
      rightButtons: [
        { label: "No", type: "button", class: "btn btn-dark mr-2", onClick: () => modalRef.dismiss() },
        {
          label: "Yes",
          type: "button",
          class: "btn btn-primary",
          onClick: (modalRef) => {
            this.loadingService.start("Clearing Sdk Message Processing Steps");
            this.quickActionsService.clearSdkMessageProcessingSteps(dto.id).subscribe(
              () => {
                this.successMessage = `Sdk message processing steps has been cleared successfully for configuration '${dto.name}'.`;
                this.loadingService.stop();
                modalRef.dismiss();
              },
              (error) => {
                this.errorMessage = `Failed to clear Sdk message processing steps for configuration '${dto.name}' due to: ${handleError(error)}`;
                this.loadingService.stop();
                modalRef.dismiss();
              }
            );
          }
        }
      ],
      onClose: () => modalRef.dismiss()
    };
  }

  resetIssues(dto: IConfigurationDto): void {

    this.errorMessage = this.successMessage = undefined;
    const modalRef = this.modalService.open(DialogComponent, {
      keyboard: false,
      ariaLabelledBy: "modalTitle"
    });
    modalRef.componentInstance.options = {
      titleText: "Reset warnings",
      type: DialogType.Warning,
      contentText:
        "Are you sure you want to reset the warnings for this configuration? This action cannot be reversed.",
      rightButtons: [
        { label: "No", type: "button", class: "btn btn-dark mr-2", onClick: () => modalRef.dismiss() },
        {
          label: "Yes",
          type: "button",
          class: "btn btn-primary",
          onClick: (modalRef) => {
            this.loadingService.start("Resetting warnings");
            this.quickActionsService.resetIssues(dto.id).subscribe(
              () => {
                this.successMessage = `Warnings have been reset successfully for configuration '${dto.name}'.`;
                this.redrawRow(dto.id);
                this.loadingService.stop();
                modalRef.dismiss();
              },
              (error) => {
                this.errorMessage = `Failed to reset warnings for configuration '${dto.name}' due to: ${handleError(error)}`;
                this.redrawRow(dto.id);
                this.loadingService.stop();
                modalRef.dismiss();
              }
            );
          }
        }
      ],
      onClose: () => modalRef.dismiss()
    };
  }

  private redrawRow(id: string) {
    const rowNode = this.gridApi.getRowNode(id);
    this.gridApi.redrawRows({ rowNodes: [rowNode] });
  }
}
